/**
 * Apps listing ([apps] shortcode): Linc Product filter tiles, search and "Load more" via AJAX.
 * The current filter/search is mirrored in the URL (?app_product=…&app_search=…) so it can be shared or reloaded.
 */
const SEARCH_DELAY = 350;

function setupApps(el) {
  const tiles = [...el.querySelectorAll('.dmn-apps__product')];
  const form = el.querySelector('.dmn-apps__search-form');
  const search = el.querySelector('.dmn-apps__search-input');
  const list = el.querySelector('.dmn-apps__list');
  const more = el.querySelector('.dmn-apps__more');
  const empty = el.querySelector('.dmn-apps__empty');
  const status = el.querySelector('.dmn-apps__status');
  const { productParam, searchParam } = el.dataset;

  let product = tiles.find((tile) => tile.getAttribute('aria-pressed') === 'true')?.dataset.product || '';
  let page = 1;
  let controller = null;
  let timer = null;

  // The no-JS fallback field; the script keeps the filter in `product` instead.
  form.querySelector(`input[type="hidden"][name="${productParam}"]`)?.remove();

  function syncUrl() {
    const url = new URL(window.location.href);
    const term = search.value.trim();

    product ? url.searchParams.set(productParam, product) : url.searchParams.delete(productParam);
    term ? url.searchParams.set(searchParam, term) : url.searchParams.delete(searchParam);

    window.history.replaceState(window.history.state, '', url);
  }

  async function load(append = false) {
    controller?.abort();
    const request = (controller = new AbortController());

    const url = new URL(el.dataset.endpoint, window.location.href);
    url.searchParams.set('page', append ? page + 1 : 1);
    url.searchParams.set('per_page', el.dataset.perPage);
    url.searchParams.set('product', product);
    url.searchParams.set('search', search.value.trim());
    url.searchParams.set('currency', el.dataset.currency);

    el.classList.add('is-loading');
    el.setAttribute('aria-busy', 'true');
    more.disabled = true;

    try {
      const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const firstNew = append ? list.children.length : 0;
      if (append) {
        list.insertAdjacentHTML('beforeend', data.html);
        // Keyboard users continue from the first new card.
        list.children[firstNew]?.querySelector('a')?.focus();
      } else {
        list.innerHTML = data.html;
      }

      page = data.page;
      empty.hidden = data.total > 0;
      more.hidden = page >= data.max_pages;
      status.textContent = el.dataset.foundText.replace('%d', data.total);
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.textContent = el.dataset.errorText;
      console.error('Apps:', error);
    } finally {
      // A newer request may have replaced this one; leave the loading state to it.
      if (controller === request) {
        el.classList.remove('is-loading');
        el.removeAttribute('aria-busy');
        more.disabled = false;
      }
    }
  }

  function refresh() {
    clearTimeout(timer);
    syncUrl();
    load();
  }

  // Single-choice toggles: picking the active tile again shows all products.
  tiles.forEach((tile) => {
    tile.addEventListener('click', () => {
      product = tile.dataset.product === product ? '' : tile.dataset.product;

      tiles.forEach((other) => {
        const active = other.dataset.product === product;
        other.setAttribute('aria-pressed', active ? 'true' : 'false');
        other.classList.toggle('is-active', active);
      });
      el.classList.toggle('has-product', product !== '');

      refresh();
    });
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    refresh();
  });

  search.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(refresh, SEARCH_DELAY);
  });

  more.addEventListener('click', () => load(true));
}

export function initApps(root = document) {
  root.querySelectorAll('.dmn-apps').forEach((el) => {
    if (el.dataset.ready) return;
    el.dataset.ready = '1';
    setupApps(el);
  });
}
